const DATA_SOURCE = 'map-draw-source'

/**
 * Manages map drawing modes by swapping between modes and managing drawn
 * feature data.
 */
class MapDrawManager {
  /**
   * Initializes a new instance of the draw manager.
   * @constructor
   * @param {Object} opts - Options for the draw manager.
   * @param {Object} opts.mapViewer - MapViewer instance containing the map.
   * @param {Object} opts.mapViewer.map - The underlying Mapbox map instance.
   */
  constructor(opts) {
    this._requireMapViewer(opts)
    this._requireMap(opts)

    this._mapViewer = opts.mapViewer
    this._map = opts.mapViewer.map
    this._mode = null
    this._modeIsSetup = false
    this._rawGeoJson = null
    this._features = []
    this._isDrawing = false
    this._eventTarget = new EventTarget()

    // Public events API matching map-x events module patterns (mapManager.events.on)
    this.events = {
      /**
       * Subscribe to a drawing event
       * @param {string} eventName - Event name
       * @param {Function} listener - Event handler
       */
      on: (eventName, listener) => {
        this._eventTarget.addEventListener(eventName, listener)
      },

      /**
       * Unsubscribe from a drawing event
       * @param {string} eventName - Event name
       * @param {Function} listener - Event handler
       */
      off: (eventName, listener) => {
        this._eventTarget.removeEventListener(eventName, listener)
      },

      /**
       * Subscribe to a drawing event once
       * @param {string} eventName - Event name
       * @param {Function} listener - Event handler
       */
      once: (eventName, listener) => {
        this._eventTarget.addEventListener(eventName, listener, { once: true })
      },
    }
  }

  /**
   * Emit a drawing event
   * @param {string} eventName - Event name
   * @param {Object} detail - Event payload
   * @private
   */
  _emit(eventName, detail) {
    this._eventTarget.dispatchEvent(new CustomEvent(eventName, { detail }))
  }

  /**
   * Returns whether drawing mode is currently active.
   * Drawing mode means user is actively editing/creating markups, not just viewing.
   * @return {boolean} True if in drawing mode, false otherwise
   */
  isDrawing() {
    return this._isDrawing
  }

  /**
   * Sets whether drawing mode is active.
   * @param {boolean} isDrawing - True to enable drawing mode, false to disable
   */
  setDrawing(isDrawing) {
    this._isDrawing = isDrawing
  }

  /**
   * Sets the GeoJSON data for the drawn data source.  Use this any time any
   * feature properties change from elsewhere in the app; like color, shape,
   * style, etc.
   * @param {Object} geoJson - Feature GeoJSON data.
   */
  setData(geoJson) {
    this._rawGeoJson = geoJson

    if (this._modeIsSetup) {
      this._renderData()
    }
  }

  /**
   * Sets the feature draw mode base type to use.  This can be switched any
   * time.
   * @param {function} Mode - A type that inherits from DrawModeBase.
   * @param {Object} opts - Arbitrary options object to pass to draw mode.
   */
  setMode(Mode, opts) {
    this._cleanupCurrentMode()
    this._initializeMode(Mode, opts)
  }

  /**
   * Ends the draw session and cleans up data.  Be sure to call this at the end
   * of drawing the feature.
   */
  endDraw() {
    this._cleanupCurrentMode()
    this._clearData()
  }

  /**
   * Validation to require that a MapViewer is passed in when initializing the
   * feature draw manager.
   * @param {Object} opts - Options object.
   * @throws Error if mapViewer is not passed.
   * @private
   */
  _requireMapViewer(opts) {
    if (!opts?.mapViewer) {
      throw new Error('an instance of a MapViewer is a required option')
    }
  }

  /**
   * Validation to require that a map is present in the MapViewer when initializing the
   * feature draw manager.
   * @param {Object} opts - Options object.
   * @throws Error if map is not present in the MapViewer.
   * @private
   */
  _requireMap(opts) {
    if (!opts?.mapViewer?.map) {
      throw new Error(
        'an instance of a Map set as a map property on the MapViewer is a required option'
      )
    }
  }

  /**
   * Renders the draw features.
   * @private
   */
  _renderData() {
    requestAnimationFrame(async () => {
      if (this._mode === null || !this._modeIsSetup) {
        return
      }

      this._features = await this._mode.toDisplayFeatures(this._rawGeoJson)

      this._map.getSource(DATA_SOURCE).setData({
        type: 'FeatureCollection',
        features: this._features,
      })
    })
  }

  /**
   * Cleans up the current draw mode.
   * @private
   */
  _cleanupCurrentMode() {
    if (this._mode !== null) {
      this._mode.onTearDown()
      this._removeEventListeners()
      this._mode = null
      this._modeIsSetup = false
    }
  }

  /**
   * Initializes a new draw mode session.
   * @param {function} Mode - A type that inherits from DrawModeBase.
   * @param {Object} opts - Arbitrary options object to pass to draw mode.
   * @private
   */
  _initializeMode(Mode, opts) {
    this._mode = new Mode()
    this._modeIsSetup = false

    if (this._rawGeoJson && !this._modeIsSetup) {
      this._mode.onSetup(this._rawGeoJson, opts)
      this._setupEventListeners()
      this._bindModeHelpers()
      this._modeIsSetup = true
      this._renderData()
    }
  }

  /**
   * Sets up the various draw mode event listeners and binds them to map events.
   * @private
   */
  _setupEventListeners() {
    const onMouseDown = this._mode.onMouseDown.bind(this._mode)
    const onMouseUp = this._mode.onMouseUp.bind(this._mode)
    const onMouseMove = this._mode.onMouseMove.bind(this._mode)
    const onMouseOut = this._mode.onMouseOut.bind(this._mode)
    const onClick = this._mode.onClick.bind(this._mode)
    const onDoubleClick = this._mode.onDoubleClick.bind(this._mode)

    this._map.on('mousedown', onMouseDown)
    this._map.on('touchstart', onMouseDown)
    this._map.on('mouseup', onMouseUp)
    this._map.on('touchend', onMouseUp)
    this._map.on('mousemove', onMouseMove)
    this._map.on('touchmove', onMouseMove)
    this._map.on('mouseout', onMouseOut)
    this._map.on('click', onClick)
    this._map.on('dblclick', onDoubleClick)

    this._removeEventListeners = () => {
      this._map.off('mousedown', onMouseDown)
      this._map.off('touchstart', onMouseDown)
      this._map.off('mouseup', onMouseUp)
      this._map.off('touchend', onMouseUp)
      this._map.off('mousemove', onMouseMove)
      this._map.off('touchmove', onMouseMove)
      this._map.off('mouseout', onMouseOut)
      this._map.off('click', onClick)
      this._map.off('dblclick', onDoubleClick)
    }
  }

  /**
   * Gets all map features at the point that are within the draw mode layers.
   * @param {Array<number>} point - X, Y screen pixel point.
   * @return {Array<Object>} List of features present at the given point.
   * @private
   */
  _getFeaturesAtPoint(point) {
    return this._map.queryRenderedFeatures(point)
  }

  /**
   * Gets the top-most feature at the given point.
   * @param {Array<number>} point - X, Y screen pixel point.
   * @return {Object} Top-most feature present at the given point.
   * @private
   */
  _getFeatureAtPoint(point) {
    return this._getFeaturesAtPoint(point)?.[0]
  }

  /**
   * Binds some helpers for draw modes.
   * @private
   */
  _bindModeHelpers() {
    this._mode.map = this._map
    this._mode.mapViewer = this._mapViewer
    this._mode.updated = this._renderData.bind(this)
    this._mode.getFeature = this._getFeature.bind(this)
    this._mode.getFeaturesAtPoint = this._getFeaturesAtPoint.bind(this)
    this._mode.getFeatureAtPoint = this._getFeatureAtPoint.bind(this)
    this._mode.setMode = this.setMode.bind(this)

    // Bind events object for consistent API pattern
    this._mode.events = {
      emit: (eventName, detail) => this._emit(eventName, detail),
    }
  }

  /**
   * Clears the markup data source.
   * @private
   */
  _clearData() {
    this._map.getSource(DATA_SOURCE).setData({
      type: 'FeatureCollection',
      features: [],
    })
  }

  /**
   * Gets a feature by ID.
   * @param {string} id - ID for the feature.
   * @return {Object} Feature GeoJSON if found, otherwise undefined.
   * @private
   */
  _getFeature(id) {
    return this._features.find((feature) => id === feature.id)
  }
}

export default MapDrawManager
